import React, { useEffect } from "react";
import { ConfirmDialog, FormValidationSummary, Stack, useNotifications, ValidationResult } from "sprint-common-web";

import { useCloudAssetDetails } from "../../../../common/hooks/useCloudAssetDetails";
import { useDeprecateCloudAsset } from "./useDeprecateCloudAsset";

interface DeprecateStatusDialogProps {
    isOpened: boolean;
    onDismiss: () => void;
}

export const DeprecateStatusDialog: React.FC<DeprecateStatusDialogProps> = ({
    isOpened,
    onDismiss
}: DeprecateStatusDialogProps) => {
    const [validationResult, setValidationResult] = React.useState<ValidationResult>();
    const [isValidating, setValidating] = React.useState(false);
    const { success, error } = useNotifications();
    const { data: assetDetails } = useCloudAssetDetails();
    const { deprecateCloudAsset, validateDeprecationCloudAsset } = useDeprecateCloudAsset({
        invalidationKey: assetDetails?.assetId
    });

    useEffect(() => {
        if (isOpened && assetDetails?.assetId) {
            setValidating(true);
            validateDeprecationCloudAsset
                .mutateAsync({ assetId: assetDetails.assetId })
                .then((result) => setValidationResult(result))
                .finally(() => setValidating(false));
        }
    }, [isOpened, assetDetails]);

    const handleSubmit = async () => {
        try {
            await deprecateCloudAsset.mutateAsync({ assetId: assetDetails?.assetId });

            success("Asset has been deprecated successfully.");
        } catch {
            error("Failed to deprecate asset.");
        } finally {
            onDismiss();
        }
    };

    return (
        <ConfirmDialog
            title={`Move to 'Deprecated'`}
            text={`Are you sure you want to deprecate individual asset?`}
            confirm={{ onClick: handleSubmit, disabled: isValidating || !validationResult?.isValid }}
            cancel={{ onClick: onDismiss }}
            hidden={!isOpened}
            width={500}
        >
            <Stack styles={{ root: { padding: "10px 0" } }}>
                <FormValidationSummary isValidating={isValidating} validationResult={validationResult} />
            </Stack>
        </ConfirmDialog>
    );
};
